import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#bindDialogCleanup();
  }

  disconnectedCallback() {
    this.#unbindDialogCleanup();

    const dialog = this.#getDialog();
    if (dialog?.open) {
      try {
        dialog.close();
      } catch (e) {}
    }

    this.#cleanupPageState();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    super.disconnectedCallback();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#bindDialogCleanup();
    }
  };

  #handleDialogClose = () => {
    this.#cleanupPageState();
  };

  #getDialog() {
    return this.querySelector('dialog');
  }

  #cleanupPageState() {
    document.documentElement.classList.remove('scroll-lock');
    document.body.style.pointerEvents = '';
  }

  #bindDialogCleanup() {
    const dialog = this.#getDialog();
    if (!dialog || dialog.__cartDrawerCleanupBound) return;

    dialog.addEventListener('close', this.#handleDialogClose);
    dialog.addEventListener('cancel', this.#handleDialogClose);
    dialog.__cartDrawerCleanupBound = true;
  }

  #unbindDialogCleanup() {
    const dialog = this.#getDialog();
    if (!dialog || !dialog.__cartDrawerCleanupBound) return;

    dialog.removeEventListener('close', this.#handleDialogClose);
    dialog.removeEventListener('cancel', this.#handleDialogClose);
    delete dialog.__cartDrawerCleanupBound;
  }

  open() {
    this.showDialog();
    this.#bindDialogCleanup();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.#cleanupPageState();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}